/**
 * Client-side request payload sanitization (defense-in-depth).
 * The backend must still validate and sanitize all input — this layer can be bypassed.
 */

// Remove null bytes; they can break storage and evade some server-side filters.
const NULL_BYTE_PATTERN = /\0/g;

// Strip script blocks and inline event handlers from strings that may be stored/displayed later.
const SCRIPT_TAG_PATTERN = /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi;
const INLINE_EVENT_HANDLER_PATTERN = /\s*on\w+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi;
const JAVASCRIPT_URL_PATTERN = /javascript:/gi;

/**
 * Sanitize a single string before it is sent to the API.
 */
export function sanitizeString(value) {
  if (typeof value !== 'string') {
    return value;
  }

  let sanitized = value.trim();
  sanitized = sanitized.replace(NULL_BYTE_PATTERN, '');
  sanitized = sanitized.replace(SCRIPT_TAG_PATTERN, '');
  sanitized = sanitized.replace(INLINE_EVENT_HANDLER_PATTERN, '');
  sanitized = sanitized.replace(JAVASCRIPT_URL_PATTERN, '');

  return sanitized;
}

/**
 * Recursively sanitize JSON-like request bodies.
 * Preserves numbers, booleans, Dates, and binary payloads unchanged.
 */
export function sanitizeRequestData(data) {
  if (data == null) {
    return data;
  }

  if (data instanceof FormData) {
    const sanitizedForm = new FormData();
    for (const [key, value] of data.entries()) {
      if (typeof value === 'string') {
        sanitizedForm.append(key, sanitizeString(value));
      } else {
        // Leave File/Blob entries untouched.
        sanitizedForm.append(key, value);
      }
    }
    return sanitizedForm;
  }

  if (typeof data === 'string') {
    return sanitizeString(data);
  }

  if (Array.isArray(data)) {
    return data.map((item) => sanitizeRequestData(item));
  }

  if (typeof data === 'object') {
    if (data instanceof Date || data instanceof Blob || data instanceof File) {
      return data;
    }

    const sanitizedObject = {};
    for (const [key, value] of Object.entries(data)) {
      sanitizedObject[key] = sanitizeRequestData(value);
    }
    return sanitizedObject;
  }

  return data;
}

/**
 * Only sanitize mutating requests that carry a body.
 */
export function shouldSanitizeRequest(config) {
  const method = (config.method || 'get').toLowerCase();
  return ['post', 'put', 'patch'].includes(method) && config.data != null;
}

/**
 * Apply sanitization to axios config.data when appropriate.
 */
export function sanitizeAxiosRequestConfig(config) {
  if (shouldSanitizeRequest(config)) {
    config.data = sanitizeRequestData(config.data);
  }
  return config;
}
